import React, { useEffect, useState } from "react";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import {
    Button,
    ButtonGroup,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Typography,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import GetAppIcon from "@material-ui/icons/GetApp";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";

const toInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (days - 1));

    return {
        startDate: toInputDate(start),
        endDate: toInputDate(end),
    };
};

const CompanyFilesModal = ({ company, open, onClose, onStatsChange }) => {
    const initialRange = getQuickRange(30);
    const [startDate, setStartDate] = useState(initialRange.startDate);
    const [endDate, setEndDate] = useState(initialRange.endDate);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const loadStats = async (rangeStart, rangeEnd) => {
        if (!company || !rangeStart || !rangeEnd) return;

        setLoading(true);
        try {
            const { data } = await api.get(`/companies/${company.id}/files`, {
                params: {
                    startDate: rangeStart,
                    endDate: rangeEnd,
                },
            });
            setStats(data.stats);
        } catch (err) {
            setStats(null);
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open || !company) return;

        const range = getQuickRange(30);
        setStartDate(range.startDate);
        setEndDate(range.endDate);
        setStats(null);
        loadStats(range.startDate, range.endDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company, open]);

    const handleQuickFilter = (days) => {
        const range = getQuickRange(days);
        setStartDate(range.startDate);
        setEndDate(range.endDate);
        setStats(null);
        loadStats(range.startDate, range.endDate);
    };

    const handleDateChange = (setter) => (event) => {
        setter(event.target.value);
        setStats(null);
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const { data } = await api.get(
                `/companies/${company.id}/files/download`,
                {
                    params: { startDate, endDate },
                    responseType: "blob",
                }
            );
            saveAs(
                data,
                `arquivos-empresa-${company.id}-${startDate}-a-${endDate}.zip`
            );
        } catch (err) {
            toastError(err);
        } finally {
            setDownloading(false);
        }
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            const { data } = await api.delete(`/companies/${company.id}/files`, {
                params: { startDate, endDate },
            });
            toast.success(`${data.deletedCount} arquivo(s) excluído(s).`);
            setStats({
                folderSize: "0 B",
                folderSizeBytes: 0,
                numberFileFolder: 0,
                updatedAtFolder: null,
            });
            onStatsChange(company.id, data.stats);
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
            setConfirmDeleteOpen(false);
        }
    };

    const hasFilteredFiles = stats && stats.numberFileFolder > 0;

    return (
        <>
            <ConfirmationModal
                title="Excluir arquivos do período?"
                open={confirmDeleteOpen}
                onClose={setConfirmDeleteOpen}
                onConfirm={handleDelete}
            >
                {stats &&
                    `${stats.numberFileFolder} arquivo(s), totalizando ${stats.folderSize}, serão excluídos permanentemente.`}
            </ConfirmationModal>

            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    Gerenciar arquivos — {company ? company.name : ""}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography color="textSecondary" gutterBottom>
                        Selecione o período de salvamento. Os arquivos não serão
                        listados na tela para evitar lentidão.
                    </Typography>

                    <Grid container spacing={2} style={{ marginTop: 4 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Data inicial"
                                type="date"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={startDate}
                                onChange={handleDateChange(setStartDate)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: endDate }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Data final"
                                type="date"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={endDate}
                                onChange={handleDateChange(setEndDate)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: startDate }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" display="block" gutterBottom>
                                Filtros rápidos
                            </Typography>
                            <ButtonGroup size="small" color="primary">
                                <Button onClick={() => handleQuickFilter(1)}>Hoje</Button>
                                <Button onClick={() => handleQuickFilter(7)}>7 dias</Button>
                                <Button onClick={() => handleQuickFilter(30)}>30 dias</Button>
                                <Button onClick={() => handleQuickFilter(90)}>90 dias</Button>
                            </ButtonGroup>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                color="primary"
                                disabled={loading || !startDate || !endDate}
                                onClick={() => loadStats(startDate, endDate)}
                            >
                                {loading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    "Consultar período"
                                )}
                            </Button>
                        </Grid>
                    </Grid>

                    <div
                        style={{
                            marginTop: 20,
                            padding: 16,
                            border: "1px solid #ddd",
                            borderRadius: 4,
                            textAlign: "center",
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={28} />
                        ) : stats ? (
                            <>
                                <Typography variant="h6">
                                    {stats.numberFileFolder} arquivo(s)
                                </Typography>
                                <Typography color="textSecondary">
                                    Tamanho total: {stats.folderSize}
                                </Typography>
                            </>
                        ) : (
                            <Typography color="textSecondary">
                                Consulte o período para habilitar as ações.
                            </Typography>
                        )}
                    </div>

                    <Grid container spacing={2} style={{ marginTop: 8 }}>
                        <Grid item xs={12} sm={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                startIcon={<GetAppIcon />}
                                disabled={!hasFilteredFiles || loading || downloading}
                                onClick={handleDownload}
                            >
                                {downloading ? "Preparando ZIP..." : "Baixar período"}
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="secondary"
                                startIcon={<DeleteOutlineIcon />}
                                disabled={!hasFilteredFiles || loading || downloading}
                                onClick={() => setConfirmDeleteOpen(true)}
                            >
                                Excluir período
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="default">
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CompanyFilesModal;
